import { useMemo } from 'react';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { UserForm, type UserFormValues } from '@/components/organisms/settings-users/UserForm';
import { useUser } from '@/hooks/settings/useUser';
import { useRoles } from '@/hooks/settings/useRoles';
import { useUpdateUser } from '@/hooks/settings/useUpdateUser';
import { useUpdateUserRoles } from '@/hooks/settings/useUpdateUserRoles';
import { useHasPermission } from '@/hooks/usePermissions';
import type { TeamUser, UpdateUserDto } from '@/types/settings';

export const Route = createFileRoute('/_dashboard/dashboard/settings/users/$id/edit')({
  component: EditUserSettingsRoute,
});

function buildUpdatePayload(user: TeamUser, values: UserFormValues): UpdateUserDto {
  const payload: UpdateUserDto = {};
  if (values.firstName.trim() !== (user.firstName ?? ''))
    payload.firstName = values.firstName.trim();
  if (values.lastName.trim() !== (user.lastName ?? '')) payload.lastName = values.lastName.trim();
  if (values.email.trim().toLowerCase() !== user.email)
    payload.email = values.email.trim().toLowerCase();
  if (values.password?.trim()) payload.password = values.password.trim();
  return payload;
}

function areArraysEqual(left: string[], right: string[]) {
  if (left.length !== right.length) return false;
  const rightSet = new Set(right);
  return left.every((value) => rightSet.has(value));
}

function EditUserSettingsRoute() {
  const { id } = Route.useParams();
  const router = useRouter();
  const decodedId = decodeURIComponent(id);
  // Fetch by id — the paginated list only loads the first page, so resolving
  // the edited user from it breaks deep links / users beyond that page.
  const { data: userData, isLoading: usersLoading } = useUser(decodedId);
  const { data: rolesData } = useRoles();
  const roles = useMemo(() => rolesData?.data ?? [], [rolesData?.data]);
  const canEdit = useHasPermission('users:edit');
  const updateUser = useUpdateUser();
  const updateRoles = useUpdateUserRoles();

  const user = userData ?? null;

  function goToDetail(userId: string) {
    void router.navigate({ to: '/dashboard/settings/users/$id', params: { id: userId } });
  }

  function close() {
    void router.navigate({ to: '/dashboard/settings/users' });
  }

  async function handleSave(userId: string, values: UserFormValues, nextRoleIds: string[]) {
    if (!user) return;

    const payload = buildUpdatePayload(user, values);
    const currentRoleIds = user.roles.map((role) => role.id);
    const hasProfileChanges = Object.keys(payload).length > 0;
    const hasRoleChanges = !areArraysEqual(currentRoleIds, nextRoleIds);

    if (hasProfileChanges) {
      await updateUser.mutateAsync({ userId, ...payload });
    }

    if (hasRoleChanges) {
      await updateRoles.mutateAsync({ userId, currentRoleIds, nextRoleIds });
    }

    close();
  }

  // `users:edit` is the gate for entering edit; if the permission is missing,
  // fall back to the read-only detail rather than rendering the form.
  if (!canEdit && user) {
    goToDetail(user.id);
    return null;
  }

  if (usersLoading && !user) {
    return <p className="px-5 py-5 text-sm text-muted-foreground">Loading user...</p>;
  }

  if (!user) {
    return <p className="px-5 py-5 text-sm text-muted-foreground">User not found.</p>;
  }

  return (
    <UserForm
      mode="edit"
      initialValues={{
        firstName: user.firstName ?? '',
        lastName: user.lastName ?? '',
        email: user.email,
        roleIds: user.roles.map((role) => role.id),
      }}
      roles={roles}
      isPending={updateUser.isPending || updateRoles.isPending}
      onBack={close}
      onSubmit={(values, nextRoleIds) => {
        void handleSave(user.id, values, nextRoleIds).catch(() => undefined);
      }}
    />
  );
}
